import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { meetingRoomsApi, MeetingRoom } from "@/lib/api/client";
import { BookingModal } from "@/components/booking-modal";
import { MeetingNav } from "@/components/meeting-nav";
import { Text, Badge, Paper, Button, Stack, SimpleGrid, Group, Box, TextInput, Select, MultiSelect } from "@mantine/core";
import { Clock, MapPin, Search } from "lucide-react";
import { AmenityIcon } from "@nodus/ui";
import { FindFreeRoomModal, RoomPriceBadge, hasPriceInfo } from "@nodus/booking";
import { filterRooms } from "@/lib/room-filter";
import { warnIfPending } from "@/lib/booking-approval-toast";
import * as Sentry from "@sentry/react";
import { SectionGuides } from "@/components/guides/section-guides";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// ---------------------------------------------------------------------------
// Quick book buttons (Mantine-styled inline replacement)
// ---------------------------------------------------------------------------

function QuickBook({
  roomId,
  freeSlotStart,
  freeSlotDurationMinutes,
  onBook,
}: {
  roomId: number;
  freeSlotStart: string;
  freeSlotDurationMinutes: number;
  onBook: (roomId: number, start: string, end: string) => void;
}) {
  const durations = [15, 30, 60].filter((d) => d <= freeSlotDurationMinutes);
  if (durations.length === 0) return null;

  return (
    <Group gap={6}>
      {durations.map((d) => {
        const endIso = new Date(new Date(freeSlotStart).getTime() + d * 60000).toISOString();
        return (
          <Button
            key={d}
            size="compact-xs"
            variant="filled"
            color="green"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onBook(roomId, freeSlotStart, endIso);
            }}
          >
            {d >= 60 ? `${d / 60}h` : `${d}m`}
          </Button>
        );
      })}
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({
  isAvailable,
  isBookable,
}: {
  isAvailable: boolean | null | undefined;
  isBookable: boolean;
}) {
  if (!isBookable) {
    return (
      <Badge size="sm" variant="filled" color="gray" radius="xl">
        Unbookable
      </Badge>
    );
  }
  if (isAvailable == null) {
    return null;
  }
  if (isAvailable) {
    return (
      <Badge size="sm" variant="filled" color="green" radius="xl">
        Available
      </Badge>
    );
  }
  return (
    <Badge size="sm" variant="filled" color="red" radius="xl">
      Booked
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Room card
// ---------------------------------------------------------------------------

function RoomCard({ room }: { room: MeetingRoom }) {
  const navigate = useNavigate();
  const [bookingSlot, setBookingSlot] = useState<{ start: string; end: string } | null>(null);

  const displayName = room.description || room.name;
  const roomNumber = room.description ? room.name : undefined;

  const hasFreeSlot =
    room.is_bookable &&
    room.next_free_slot_start != null &&
    room.next_free_slot_duration_minutes != null;

  return (
    <>
      <Paper
        withBorder
        shadow="xs"
        radius="md"
        p="md"
        onClick={() => navigate(`/meeting-rooms/${room.id}`)}
        style={{
          cursor: "pointer",
          transition: "box-shadow 0.15s ease, transform 0.15s ease",
          opacity: !room.is_bookable ? 0.65 : 1,
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
          e.currentTarget.style.boxShadow = "var(--mantine-shadow-md)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
          e.currentTarget.style.boxShadow = "var(--mantine-shadow-xs)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <Stack gap="sm">
          {/* Header: name + status badge */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Box miw={0}>
              <Text fw={600} fz="md" truncate="end">
                {displayName}
              </Text>
              {roomNumber && roomNumber !== displayName && (
                <Text fz="xs" c="dimmed">
                  {roomNumber}
                </Text>
              )}
            </Box>
            <StatusBadge
              isAvailable={room.is_available}
              isBookable={room.is_bookable}
            />
          </Group>

          {/* Amenity icons — icon, colour and wording all come from
              @nodus/ui's AMENITY_CONFIG; size 12 + radius "xl" reproduce the
              pill this page used before the consolidation. */}
          <Group gap="md">
            {room.seats != null && room.seats > 0 && (
              <AmenityIcon type="seat" count={room.seats} size={12} radius="xl" />
            )}
            {room.has_camera && <AmenityIcon type="camera" size={12} radius="xl" />}
            {room.has_tv && <AmenityIcon type="tv" size={12} radius="xl" />}
            {room.has_speaker && <AmenityIcon type="speaker" size={12} radius="xl" />}
          </Group>

          {hasPriceInfo(room.pricing) && (
            <Box>
              <RoomPriceBadge pricing={room.pricing} />
            </Box>
          )}

          {/* Show on map link */}
          {room.has_floorplan && (
            <Group gap={4}>
              <MapPin size={13} color="var(--mantine-color-blue-5)" />
              <Text
                fz="xs"
                c="blue"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  navigate(
                    `/floorplan?room=${room.id}${room.floorplan_id ? `&fp=${room.floorplan_id}` : ""}`,
                  );
                }}
                style={{ cursor: "pointer" }}
                td="hover:underline"
              >
                Show on map
              </Text>
            </Group>
          )}

          {/* Current event (when booked) */}
          {room.is_available === false && room.current_event && (
            <Box
              pl="sm"
              style={{
                borderLeft: "3px solid var(--mantine-color-red-5)",
              }}
            >
              <Text fz="sm" fw={500} truncate="end">
                {room.current_event.title || "Booked"}
              </Text>
              <Text fz="xs" c="dimmed">
                {formatTime(room.current_event.start)} &ndash; {formatTime(room.current_event.end)}
              </Text>
              {room.current_event.organizer_tenant_name && (
                <Text fz="xs" c="dimmed">
                  {room.current_event.organizer_tenant_name}
                </Text>
              )}
            </Box>
          )}

          {/* Status messages */}
          {!room.is_bookable ? (
            <Text fz="sm" c="dimmed">
              Not available for booking
            </Text>
          ) : room.is_available ? (
            <Text fz="sm" fw={600} c="green">
              Available now
            </Text>
          ) : null}

          {/* Next free slot + quick book */}
          {hasFreeSlot && (
            <Stack gap={6}>
              <Group gap={6}>
                <Clock size={14} color="var(--mantine-color-dimmed)" />
                <Text fz="sm" c="dimmed">
                  {room.is_available
                    ? `Free for ${formatDuration(room.next_free_slot_duration_minutes!)}`
                    : `Next free: ${formatTime(room.next_free_slot_start!)} (${formatDuration(room.next_free_slot_duration_minutes!)})`}
                </Text>
              </Group>
              {room.is_bookable && (
                <QuickBook
                  roomId={room.id}
                  freeSlotStart={room.next_free_slot_start!}
                  freeSlotDurationMinutes={room.next_free_slot_duration_minutes!}
                  onBook={(_id, start, end) => setBookingSlot({ start, end })}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {bookingSlot && (
        <BookingModal
          opened={!!bookingSlot}
          onClose={() => setBookingSlot(null)}
          roomId={room.id}
          roomName={displayName}
          initialStart={bookingSlot.start}
          initialEnd={bookingSlot.end}
          pricing={room.pricing}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function MeetingRoomsPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["meeting-rooms"],
    queryFn: () => meetingRoomsApi.getRooms(),
    staleTime: 0,
    refetchInterval: 60_000,
  });

  const rooms = data?.data ?? [];

  const [query, setQuery] = useState("");
  const [minSeats, setMinSeats] = useState<string | null>(null);
  const [equipment, setEquipment] = useState<string[]>([]);

  const minSeatsNum = minSeats ? parseInt(minSeats, 10) : null;
  const filteredRooms = filterRooms(rooms, { query, minSeats: minSeatsNum, equipment });

  const [findFreeOpen, setFindFreeOpen] = useState(false);
  const fetchFreeRooms = useCallback(async (durationMinutes: number) => {
    const res = await meetingRoomsApi.findFreeRooms(durationMinutes);
    return res.data;
  }, []);
  const bookFreeRoom = useCallback(async (roomId: number, minutes: number) => {
    const start = new Date();
    const end = new Date(start.getTime() + minutes * 60_000);
    const res = await meetingRoomsApi.bookRoom(roomId, {
      title: `Booking (${minutes} min)`,
      start: start.toISOString(),
      end: end.toISOString(),
      description: "Booked via Find a free meeting room",
      source: "member_find_free",
    } as any);
    warnIfPending(res.data);
    return { end: res.data.end };
  }, []);
  const fetchWayfinding = useCallback(async (roomId: number) => {
    const res = await meetingRoomsApi.wayfind(roomId);
    return res.data;
  }, []);

  return (
    <Stack p="md" gap="md">
      <MeetingNav />
      <SectionGuides area="meeting_rooms" />

      <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
        <Group align="flex-end" wrap="wrap" gap="sm">
          <TextInput
            leftSection={<Search size={16} />}
            placeholder="Search by name or number"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            aria-label="Search rooms"
            w={{ base: "100%", sm: 240 }}
          />
          <Select
            placeholder="Seats"
            aria-label="Minimum seats"
            value={minSeats}
            onChange={setMinSeats}
            clearable
            w={{ base: "100%", sm: 130 }}
            data={[
              { value: "2", label: "2+ seats" },
              { value: "4", label: "4+ seats" },
              { value: "6", label: "6+ seats" },
              { value: "8", label: "8+ seats" },
              { value: "10", label: "10+ seats" },
            ]}
          />
          <MultiSelect
            placeholder={equipment.length ? undefined : "Equipment"}
            aria-label="Equipment"
            value={equipment}
            onChange={setEquipment}
            clearable
            w={{ base: "100%", sm: 220 }}
            data={[
              { value: "camera", label: "Camera" },
              { value: "display", label: "Display" },
              { value: "speaker", label: "Speaker" },
            ]}
          />
        </Group>
        <Button
          variant="light"
          leftSection={<Search size={16} />}
          onClick={() => setFindFreeOpen(true)}
        >
          Find a free meeting room
        </Button>
      </Group>

      {isLoading && <Text fz="sm" c="dimmed">Loading meeting rooms...</Text>}
      {isError && <Text fz="sm" c="red">Failed to load meeting rooms.</Text>}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {filteredRooms.map((room) => (
          <RoomCard key={room.id} room={room} />
        ))}
      </SimpleGrid>

      {!isLoading && rooms.length === 0 && (
        <Text fz="sm" c="dimmed" ta="center" py="xl">
          No meeting rooms available. If you expect to see rooms here, contact your office manager about your floor access.
        </Text>
      )}

      {!isLoading && rooms.length > 0 && filteredRooms.length === 0 && (
        <Text fz="sm" c="dimmed" ta="center" py="xl">
          No rooms match your search and filters.
        </Text>
      )}

      <FindFreeRoomModal
        opened={findFreeOpen}
        onClose={() => setFindFreeOpen(false)}
        fetchFreeRooms={fetchFreeRooms}
        bookRoom={bookFreeRoom}
        fetchWayfinding={fetchWayfinding}
        onError={(err, context) =>
          Sentry.captureException(err, { tags: { feature: "fafr", context } })
        }
      />
    </Stack>
  );
}
